import { AxiosError } from 'axios';

// Define types for error response shapes
interface ErrorWithMessage {
  message: string;
}

interface ErrorWithErrorField {
  error: string;
}

interface ErrorWithDetail {
  detail: string;
}

interface ErrorWithTitle {
  title: string;
}

// Type guards as arrow functions
const hasMessage = (obj: unknown): obj is ErrorWithMessage =>
  typeof obj === 'object' && obj !== null && 'message' in obj;

const hasError = (obj: unknown): obj is ErrorWithErrorField =>
  typeof obj === 'object' && obj !== null && 'error' in obj;

const hasDetail = (obj: unknown): obj is ErrorWithDetail =>
  typeof obj === 'object' && obj !== null && 'detail' in obj;

const hasTitle = (obj: unknown): obj is ErrorWithTitle =>
  typeof obj === 'object' && obj !== null && 'title' in obj;

// Handle HTTP status code based messages
const getStatusCodeMessage = (status: number): string | null => {
  if (status === 401) return 'Authentication error. Please log in again.';
  if (status === 403) return 'You do not have permission to perform this action.';
  if (status === 404) return 'The requested resource was not found.';
  if (status >= 500) return 'Server error. Please try again later.';
  return null;
};

// Extract message from any data object
const extractMessageFromData = (data: unknown): string | null => {
  if (typeof data === 'string') return data;
  if (hasMessage(data)) return data.message;
  if (hasDetail(data)) return data.detail;
  if (hasTitle(data)) return data.title;
  if (hasError(data)) return data.error;
  return null;
};

// Handle Axios specific errors
const handleAxiosError = (error: AxiosError): string | null => {
  // Check for response with status code
  if (error.response) {
    const dataMessage = extractMessageFromData(error.response.data);
    if (dataMessage) return dataMessage;

    const statusMessage = getStatusCodeMessage(error.response.status);
    if (statusMessage) return statusMessage;
  }

  // Check for network errors
  if (error.message) {
    if (error.message === 'Network Error') {
      return 'Network error. Please check your connection.';
    }
    return error.message;
  }

  return null;
};

// Handle RTK Query SerializedError format
// RTK Query's unwrap() throws errors with a 'data' property containing the API response
const handleRTKQueryError = (error: unknown): string | null => {
  // Check if error has a 'data' property (RTK Query SerializedError format)
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const dataMessage = extractMessageFromData((error as { data: unknown }).data);
    if (dataMessage) return dataMessage;
  }

  // Check if error itself has a message property (standard Error object)
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message;
  }

  return null;
};

// Main error message function
export const getErrorMessage = (error: unknown): string => {
  // Handle Axios errors
  if (error instanceof AxiosError) {
    const axiosErrorMessage = handleAxiosError(error);
    if (axiosErrorMessage) return axiosErrorMessage;
  }

  // Handle RTK Query errors (SerializedError format)
  const rtkErrorMessage = handleRTKQueryError(error);
  if (rtkErrorMessage) return rtkErrorMessage;

  // Fallback generic error message
  return 'An unexpected error occurred.';
};
